import React from "react";
import { Layout } from "../Layout/Layout";
import { createBrowserRouter, Outlet, redirect, RouteObject } from "react-router-dom";
import { RouteErrorBoundary } from "../Components/AppErrorBoundary";
import { RouterPiggyBank } from "./routerPiggyBank";
import { apiUrlV1 } from "../config/config";
import { RouterFortuneWheel } from "./routerFortuneWheel";
import { RouterCoinFlip } from "./routeCoinFlip";
import { RouterJackpot } from "./routerJackpot";
import { RouterCases } from "./routerCases";
import { RouterCusomGame } from "./routerCustomeGame";
import { AuthPage } from "../Pages/Auth/AuthPage";
import { SignedInAuthRedirect, SuperAdminAuth } from "./SuperAdminAuth";
import { SetPasswordPage } from "../Pages/SetPassword/SetPasswordPage";

const MissionsPage = React.lazy(
  async () => await import("../Pages/Missions/MissionsPage")
);

const DocsPage = React.lazy(
  async () => await import("../Pages/Docs/DocsPage")
);

const MainPage = React.lazy(
  async () => await import("../Pages/Games/GamesPage")
);
const TriggerPage = React.lazy(
  async () => await import("../Pages/Trigger/TriggerPage")
);
const BonusPage = React.lazy(
  async () => await import("../Pages/Bonus/BonusPage")
);
const CohortesPage = React.lazy(
  async () => await import("../Pages/Cohortes/CohortesPage")
);
const CurrenciesPage = React.lazy(
  async () => await import("../Pages/Currencies/CurrenciesPage")
);
const UsersPage = React.lazy(
  async () => await import("../Pages/Users/UsersPage")
);
const UserDetailPage = React.lazy(
  async () => await import("../Pages/Users/UserDetailPage")
);
const BannersPage = React.lazy(
  async () => await import("../Pages/Banners/BannersPage")
);
const BannerEditorPage = React.lazy(
  async () => await import("../Pages/Banners/BannerEditorPage")
);
const BannersV3Page = React.lazy(
  async () => await import("../Pages/BannersV3/BannersV3Page")
);
const BannerV3EditorPage = React.lazy(
  async () => await import("../Pages/BannersV3/BannerV3EditorPage")
);
const IntegrationPage = React.lazy(
  async () => await import("../Pages/Integration/IntegrationPage")
);

const IntegrationEventsPage = React.lazy(
  async () => await import("../Pages/IntegrationEvents/IntegrationEventsPage")
);

const WebhookPage = React.lazy(
  async () => await import("../Pages/Webhook/WebhookPage")
);
const WebhookHistoryPage = React.lazy(
  async () => await import("../Pages/Webhook/WebhookHistory/WebhookHistoryPage")
);

const EventsPage = React.lazy(
  async () => await import("../Pages/Events/EventsPage")
);

const NotFoundPage = React.lazy(
  async () => await import("../Pages/NotFound/NotFoundPage")
);

const AnalyticsPage = React.lazy(
  async () => await import("../Pages/Analytics/AnalyticsPage")
);

const RulesPage = React.lazy(
  async () => await import("../Pages/Rules/RulesPage")
);


export const unauthorizedRouter = createBrowserRouter([
  {
    errorElement: <RouteErrorBoundary />,
    path: "*",
    element: <AuthPage />,
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/auth",
    element: <SuperAdminAuth />,
  },
  // One-time "set your password" link: public, also declared for signed-in users below.
  {
    errorElement: <RouteErrorBoundary />,
    path: "/set-password",
    element: <SetPasswordPage />,
  },
]);

const appRoutes: RouteObject[] = [
  {
    errorElement: <RouteErrorBoundary />,
    path: "*",
    element: (
      <Layout>
        <NotFoundPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/auth",
    element: <SignedInAuthRedirect />,
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/set-password",
    element: <SetPasswordPage />,
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/rules",
    element: (
      <Layout>
        <RulesPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/analytics",
    element: (
      <Layout>
        <AnalyticsPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/",
    element: (
      <Layout>
        <MainPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/bonus",
    element: (
      <Layout>
        <BonusPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/cohortes",
    element: (
      <Layout>
        <CohortesPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/currencies",
    element: (
      <Layout>
        <CurrenciesPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/users",
    element: (
      <Layout>
        <UsersPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/users/:customerUserId",
    element: (
      <Layout>
        <UserDetailPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/banners",
    element: (
      <Layout>
        <BannersPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/banners/:id",
    element: (
      <Layout>
        <BannerEditorPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/banners-v3",
    element: (
      <Layout>
        <BannersV3Page />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/banners-v3/:id",
    element: (
      <Layout>
        <BannerV3EditorPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/trigger",
    element: (
      <Layout>
        <TriggerPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/game",
    loader: async () => {
      return redirect(`/`);
    },
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/integrations",
    element: (
      <Layout>
        <IntegrationPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/integration-events",
    element: (
      <Layout>
        <IntegrationEventsPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/webhook",
    element: (
      <Layout>
        <WebhookPage />
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "events",
    loader: async () => {
      const { token } = localStorage;
      return await fetch(
        `${apiUrlV1}/organization/user-events/read/available-events-types-list`,
        {
          method: "GET",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    },
    element: (
      <Layout>
        <EventsPage />
      </Layout>
    ),
  },
  //
  {
    errorElement: <RouteErrorBoundary />,
    path: "/webhook/:id",
    element: (
      <Layout>
        <WebhookHistoryPage />
      </Layout>
    ),
  },

  {
    errorElement: <RouteErrorBoundary />,
    path: "/missions",
    element: (
      <Layout>
        <MissionsPage />
      </Layout>
    ),
  },
  ...RouterCases,
  ...RouterJackpot,
  ...RouterPiggyBank,
  ...RouterFortuneWheel,
  ...RouterCoinFlip,
  ...RouterCusomGame,
  {
    errorElement: <RouteErrorBoundary />,
    path: "/docs",
    element: (
      <Layout>
        <React.Suspense fallback={null}>
          <DocsPage />
        </React.Suspense>
      </Layout>
    ),
  },
  {
    errorElement: <RouteErrorBoundary />,
    path: "/docs/:sectionId/:subsectionId",
    element: (
      <Layout>
        <React.Suspense fallback={null}>
          <DocsPage />
        </React.Suspense>
      </Layout>
    ),
  },
];

/**
 * The pages are declared each in its own `<Layout>`, but they must share ONE: a layout per route
 * is torn down and rebuilt on every section change, so the sidebar and everything around the
 * page flash and lose their state — the whole admin looked like it reloaded. Here every page
 * declared with a layout becomes a child of a single layout route; the rest stay as they are.
 */
const isInLayout = (route: RouteObject): boolean =>
  React.isValidElement(route.element) && route.element.type === Layout;

const withoutLayout = (route: RouteObject): RouteObject => ({
  ...route,
  element: (route.element as React.ReactElement<{ children: React.ReactNode }>).props.children,
});

export const router = createBrowserRouter([
  ...appRoutes.filter((r) => !isInLayout(r)),
  {
    errorElement: <RouteErrorBoundary />,
    element: (
      <Layout>
        <Outlet />
      </Layout>
    ),
    children: appRoutes.filter(isInLayout).map(withoutLayout),
  },
]);

